import React from "react";
import { InlineMath } from "react-katex";
import {
  makeSimpleClosedTask,
  normalizeMay2026Variant,
  pick,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const VARIANTS = [
  { A: 2, B: 3, C: 3, D: 5 },
  { A: 4, B: 2, C: 2, D: 7 },
  { A: 5, B: 5, C: 5, D: 4 },
  { A: 3, B: 2, C: 2, D: 4 },
  { A: 2, B: 4, C: 4, D: 3 },
  { A: 5, B: 3, C: 3, D: 6 },
  { A: 4, B: 6, C: 6, D: 3 },
  { A: 6, B: 4, C: 4, D: 5 },
];

function gcd(a, b) {
  let left = Math.abs(a);
  let right = Math.abs(b);
  while (right) [left, right] = [right, left % right];
  return left || 1;
}

function fractionLatex(numerator, denominator) {
  const divisor = gcd(numerator, denominator);
  const reducedNumerator = numerator / divisor;
  const reducedDenominator = denominator / divisor;
  if (reducedDenominator === 1) return String(reducedNumerator);
  const sign = reducedNumerator * reducedDenominator < 0 ? "-" : "";
  return `${sign}\\dfrac{${Math.abs(reducedNumerator)}}{${Math.abs(reducedDenominator)}}`;
}

function withAnswers(values, official = false) {
  const numerator = values.C * values.D - values.B;
  return {
    ...values,
    result: fractionLatex(numerator, values.A),
    wrong: official
      ? ["\\dfrac{5}{8}", "\\dfrac{8}{3}", "\\dfrac{32}{5}"]
      : [
        fractionLatex(values.C * values.D, values.A),
        fractionLatex(values.C * values.D + values.B, values.A),
        fractionLatex(values.A, numerator),
      ],
  };
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const values = variant === "original"
    ? { A: 3, B: 4, C: 4, D: 6 }
    : pick(VARIANTS);
  const data = withAnswers(values, variant === "original");

  return makeSimpleClosedTask({
    type: "MaturaTrigTanFromLinearRelationClosedTask",
    taskNumber: "23",
    variant,
    prompt: <>Kąt <InlineMath math="\alpha" /> jest ostry i spełnia równanie:</>,
    latex: `\\frac{${data.A}\\sin\\alpha+${data.B}\\cos\\alpha}{${data.C}\\cos\\alpha}=${data.D}`,
    correct: data.result,
    wrong: data.wrong,
    tags: ["trygonometria", "tożsamości trygonometryczne", "tangens"],
    data: {
      A: data.A,
      B: data.B,
      C: data.C,
      D: data.D,
      result: data.result,
      afterLatex: <>Tangens kąta <InlineMath math="\alpha" /> jest równy:</>,
    },
    explanationSteps: [
      {
        title: "Rozdzielamy ułamek na sumę dwóch ułamków.",
        latex: `\\frac{${data.A}\\sin\\alpha}{${data.C}\\cos\\alpha}+\\frac{${data.B}\\cos\\alpha}{${data.C}\\cos\\alpha}=${data.D}`,
      },
      {
        title: "Skracamy cosinus i korzystamy z definicji tangensa.",
        latex: `\\frac{${data.A}}{${data.C}}\\cdot\\frac{\\sin\\alpha}{\\cos\\alpha}+\\frac{${data.B}}{${data.C}}=${data.D}\\quad\\Longrightarrow\\quad\\frac{${data.A}}{${data.C}}\\operatorname{tg}\\alpha+\\frac{${data.B}}{${data.C}}=${data.D}`,
      },
      {
        title: <>Rozwiązujemy równanie z niewiadomą <InlineMath math="\operatorname{tg}\alpha" />.</>,
        latex: `${data.A}\\operatorname{tg}\\alpha=${data.C * data.D}-${data.B}=${data.C * data.D - data.B}\\quad\\Longrightarrow\\quad\\operatorname{tg}\\alpha=${data.result}`,
      },
    ],
  });
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedTask as renderTask,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
